import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLines, EASE } from "@/components/site/Motion";
import { LogoMark } from "@/components/site/Logo";
import { HeroVisual } from "./HeroVisual";
import { scrollToId } from "@/components/site/SmoothScroll";

const BADGES = ["Penningtvättlagen", "Auktoriserade redovisningskonsulter", "Servrar i Sverige"];

const fade = (delay) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, ease: EASE, delay } });

export const Hero = () => (
  <section className="relative overflow-hidden -mt-20 pt-28 lg:pt-36 pb-20 lg:pb-28 grid-lines" data-testid="hero-section">
    <div aria-hidden="true" className="absolute -right-40 -top-20 opacity-[0.07] pointer-events-none">
      <LogoMark className="w-[720px] h-[720px]" animate />
    </div>
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
      <div className="lg:col-span-7">
        <motion.p {...fade(0.05)} className="inline-flex items-center gap-2 rounded-full border border-[#D8D7CE] bg-white/70 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[#0B14A0]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#19C3CF] animate-pulse" /> Molntjänst för redovisningskonsulter
        </motion.p>
        <MaskedLines
          testId="hero-title"
          delay={0.15}
          className="font-display font-bold tracking-[-0.035em] leading-[0.95] text-[#070B2E] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.6rem] mt-8"
          lines={["Kvalitet i", <span className="text-brand-gradient">varje uppdrag.</span>, "Kontroll över", "varje kund."]}
        />
        <motion.p {...fade(0.75)} className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#3A3F5C]">
          Redoc kvalitetssäkrar byråns uppdrag, hjälper dig uppfylla Penningtvättlagen och stämmer av kontoutdrag med AI – skapat utifrån över 38 års erfarenhet som redovisningskonsult och företagare samt 18 års erfarenhet som kvalitetskontrollant av auktoriserade redovisningskonsulter.
        </motion.p>
        <motion.div {...fade(0.9)} className="mt-10 flex flex-wrap gap-3">
          <Link to="/kontakt?subject=Boka%20demo" data-testid="hero-cta-demo" className="group inline-flex items-center gap-3 rounded-full bg-[#0B14A0] text-white pl-7 pr-2 py-2 font-semibold transition-[background-color,box-shadow] duration-300 hover:bg-[#070B2E] hover:shadow-[0_18px_40px_-12px_rgba(11,20,160,0.6)]">
            Boka demo
            <span className="grid place-items-center w-10 h-10 rounded-full bg-white text-[#0B14A0] transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="w-4 h-4" /></span>
          </Link>
          <button onClick={() => scrollToId("produkter")} data-testid="hero-cta-products" className="inline-flex items-center gap-2 rounded-full border border-[#070B2E]/20 bg-white/60 px-7 py-4 font-semibold text-[#070B2E] transition-[background-color,border-color] duration-300 hover:bg-white hover:border-[#070B2E]/40">
            Utforska produkterna <ArrowDown className="w-4 h-4" />
          </button>
        </motion.div>
        <motion.ul {...fade(1.05)} className="mt-12 flex flex-wrap gap-x-6 gap-y-3" data-testid="hero-trust-badges">
          {BADGES.map((b) => (
            <li key={b} className="flex items-center gap-2 text-sm font-medium text-[#1C2150]">
              <span className="w-5 h-5 grid place-items-center rounded-full bg-[#19C3CF]/15 text-[#0B14A0] text-[11px]">✓</span>{b}
            </li>
          ))}
        </motion.ul>
      </div>
      <div className="lg:col-span-5 px-4 sm:px-10 lg:px-0">
        <HeroVisual />
      </div>
    </div>
  </section>
);
